<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/searchwithin';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '@react-spectrum/searchwithin/package.json';

```jsx import
import {SearchWithin} from '@react-spectrum/searchwithin';
import {SearchField} from '@react-spectrum/searchfield';
import {Picker, Item} from '@react-spectrum/picker';
```

---
category: Forms
keywords: [search within]
after_version: 3.0.0
---

# SearchWithin

<p>{docs.exports.SearchWithin.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['SearchWithin']} />

## Example

```tsx example
<SearchWithin label="Search">
  <SearchField placeholder="Search" />
  <Picker defaultSelectedKey="all">
    <Item key="all">All</Item>
    <Item key="campaigns">Campaigns</Item>
    <Item key="audiences">Audiences</Item>
    <Item key="tags">Tags</Item>
  </Picker>
</SearchWithin>
```

## Content

SearchWithin accepts exactly two children: a [Search Field](SearchField.html) and a [Picker](SearchField.html). The SearchField element should be the first child passed into the SearchWithin.

## Labeling

SearchWithin can be labeled using the `label` prop. This will add a visible label, and be used by the SearchField and the Picker's aria-labelledby, as well as by the group's aria-label.

## Internationalization

In order to internationalize a SearchWithin, a localized string should be passed to the label prop. When the necessityIndicator prop is set to "label", a localized string will be provided for "(required)" or "(optional)" automatically.

## Props

<PropTable component={docs.exports.SearchWithin} links={docs.links} />

## Visual options

### Disabled

```tsx example
<SearchWithin label="Search" isDisabled>
  <SearchField placeholder="Search" />
  <Picker defaultSelectedKey="all">
    <Item key="all">All</Item>
    <Item key="campaigns">Campaigns</Item>
    <Item key="audiences">Audiences</Item>
    <Item key="tags">Tags</Item>
  </Picker>
</SearchWithin>
```

### Read only

```tsx example
<SearchWithin label="Search">
  <SearchField placeholder="Search" value="Read Only" isReadOnly />
  <Picker defaultSelectedKey="all">
    <Item key="all">All</Item>
    <Item key="campaigns">Campaigns</Item>
    <Item key="audiences">Audiences</Item>
    <Item key="tags">Tags</Item>
  </Picker>
</SearchWithin>
```

### Label Position

```tsx example
<SearchWithin label="Search" labelPosition="side">
  <SearchField placeholder="Search" />
  <Picker defaultSelectedKey="all">
    <Item key="all">All</Item>
    <Item key="campaigns">Campaigns</Item>
    <Item key="audiences">Audiences</Item>
    <Item key="tags">Tags</Item>
  </Picker>
</SearchWithin>
```

### Custom width

```tsx example
<SearchWithin label="Search" width="size-3600" >
  <SearchField placeholder="Search" />
  <Picker defaultSelectedKey="all">
    <Item key="all">All</Item>
    <Item key="campaigns">Campaigns</Item>
    <Item key="audiences">Audiences</Item>
    <Item key="tags">Tags</Item>
  </Picker>
</SearchWithin>
```

